<template>
    <PageWrapper>
        <template #actions>
            <BackButton
                @click="
                    router.push({
                        name: 'platform.customize',
                        params: { slug: platformSlug },
                    })
                "
            />
            <LangSwitch v-model:activeLocale="activeLocale" />
            <button
                class="btn btn-primary space-x-2 bg-green-500 text-white"
                @click="openSidebar"
            >
                <fa icon="fa-cog" />
                <p>{{ i18n.t('config') }}</p>
            </button>
            <SaveButton
                :disabled="!canBeSaved"
                @click="savePage"
            />
            <button
                class="btn btn-primary space-x-2 bg-blue-500 text-white"
                @click="previewPage"
            >
                <fa icon="fa-eye" />
                <p>{{ i18n.t('preview') }}</p>
            </button>
        </template>
        <div class="space-y-8">
            <template v-for="block in blocks">
                <CardsForm
                    v-if="block.type === 'cards'"
                    :id="block.id"
                    :key="block.id"
                    :active-locale="activeLocale"
                    :initial-data="getBlockData(block.id)"
                    @update:form="updateBlock"
                />
                <PageHeaderForm
                    v-else-if="block.type === 'pageHeader'"
                    :id="block.id"
                    :key="block.id"
                    :active-locale="activeLocale"
                    :initial-data="getBlockData(block.id)"
                    @update:form="updateBlock"
                />
            </template>
        </div>
    </PageWrapper>
    <PageBuilderSidebar
        :open="sidebarOpen"
        :active-locale="activeLocale"
        @close="sidebarOpen = false"
    />
</template>

<script setup lang="ts">
import { Block, BlockType, ImageField, LocaleMap } from '@builder/types'
import { emptyLocaleMap, SUPPORTED_LOCALES } from '@builder/defaults'
import BackButton from '@src/partials/actions/BackButton.vue'
import SaveButton from '@src/partials/actions/SaveButton.vue'
import CardsForm from '@src/partials/pageBuilder/forms/CardsForm.vue'
import PageHeaderForm from '@src/partials/pageBuilder/forms/PageHeaderForm.vue'
import LangSwitch from '@src/partials/pageBuilder/LangSwitch.vue'
import PageBuilderSidebar from '@src/partials/pageBuilder/PageBuilderSidebar.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'
import {
    createPreviewToken,
    getPageBySlug,
    updatePage,
} from '@src/utils/api/pages'
import { useToastStore } from '@src/utils/stores/toast'
import { useEventBus } from '@vueuse/core'
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import useBuilder from '@src/composables/builder/useBuilder'

const route = useRoute()
const router = useRouter()
const toast = useToastStore()
const i18n = useI18n()
const { getDefaultBlockData, generateBlockKey } = useBuilder()

const pageSlug = ref(route.params.pageSlug as string)
const platformSlug = route.params.slug as string
const activeLocale = ref<(typeof SUPPORTED_LOCALES)[number]>(
    SUPPORTED_LOCALES[0]
)

const blocks = ref<{ type: BlockType; id: string }[]>([])
const blocksData = ref<Block[]>([])
const canBeSaved = ref(false)

const blockBus = useEventBus('blockActions')
const seoBus = useEventBus('seoActions')

const seoData = ref({
    status: 'draft' as 'draft' | 'published',
    title: emptyLocaleMap() as LocaleMap,
    description: emptyLocaleMap() as LocaleMap,
    slug: emptyLocaleMap() as LocaleMap,
    og_title: emptyLocaleMap() as LocaleMap,
    og_description: emptyLocaleMap() as LocaleMap,
    og_image: null as ImageField | null,
    focus_keyword: emptyLocaleMap() as LocaleMap,
})

seoBus.on((event) => {
    const e = event as { type?: string } & typeof seoData.value
    if (e.type === 'init') return
    seoData.value = e
    canBeSaved.value = true
})

blockBus.on((event) => {
    const { type, id, block } = event as {
        type: string
        id: string
        block: BlockType
    }
    const i = blocks.value.findIndex((b) => b.id === id)

    if (type === 'removeBlock') {
        if (i !== -1) blocks.value.splice(i, 1)
        const di = blocksData.value.findIndex((b) => b.id === id)
        if (di !== -1) blocksData.value.splice(di, 1)
        canBeSaved.value = true
    } else if (type === 'moveBlockUp' && i > 0) {
        ;[blocks.value[i - 1], blocks.value[i]] = [
            blocks.value[i],
            blocks.value[i - 1],
        ]
        canBeSaved.value = true
    } else if (
        type === 'moveBlockDown' &&
        i !== -1 &&
        i < blocks.value.length - 1
    ) {
        ;[blocks.value[i], blocks.value[i + 1]] = [
            blocks.value[i + 1],
            blocks.value[i],
        ]
        canBeSaved.value = true
    } else if (type === 'addBlock') {
        const defaultBlock = getDefaultBlockData(block)
        blocks.value.push({ type: block, id: defaultBlock.id as string })
        blocksData.value.push(defaultBlock)
        canBeSaved.value = true
    }
})

const getBlockData = <T extends Block>(id: string): T | undefined => {
    if (blocksData.value && blocksData.value.length > 0) {
        return blocksData.value.find((b) => b.id === id) as T
    }
    return undefined
}

const updateBlock = (block: Block) => {
    const index = blocksData.value.findIndex((b) => b.id === block.id)
    if (index !== -1) {
        blocksData.value[index] = block
    } else {
        blocksData.value.push(block)
    }
    canBeSaved.value = true
}

const sidebarOpen = ref(false)

const savePage = async () => {
    try {
        const orderedBlocks = blocks.value
            .map((b) => blocksData.value.find((d) => d.id === b.id))
            .filter((b): b is Block => b !== undefined)
        const page = {
            id: pageSlug.value,
            blocks: orderedBlocks,
            ...seoData.value,
        }
        await updatePage(page)
        canBeSaved.value = false
        toast.success(i18n.t('toast_success_page_saved'))
        const newSlug = seoData.value.slug.de
        if (newSlug && newSlug !== pageSlug.value) {
            pageSlug.value = newSlug
            router.replace({
                name: 'platform.customize.page',
                params: { slug: platformSlug, pageSlug: newSlug },
            })
        }
    } catch {
        // global interceptor shows error toast; keep canBeSaved true so user can retry
    }
}

const previewPage = async () => {
    if (seoData.value.status === 'draft' && canBeSaved.value) {
        await savePage()
    }
    const orderedBlocks = blocks.value
        .map((b) => blocksData.value.find((d) => d.id === b.id))
        .filter((b): b is Block => b !== undefined)
    const response = await createPreviewToken({
        id: pageSlug.value,
        blocks: orderedBlocks,
        ...seoData.value,
    })
    if (response.status === 200) {
        window.open(
            `${import.meta.env.VITE_FRONTEND_URL}/preview/${
                response.data.token
            }`,
            '_blank'
        )
    }
}

const openSidebar = () => {
    sidebarOpen.value = true
}

const withAllLocales = (
    value: Partial<LocaleMap> | null | undefined
): LocaleMap => {
    const normalized = emptyLocaleMap()

    for (const locale of SUPPORTED_LOCALES) {
        normalized[locale] = value?.[locale] ?? ''
    }

    return normalized
}

onMounted(async () => {
    const response = await getPageBySlug(pageSlug.value)
    if (response.status === 200) {
        const page = response.data
        blocksData.value = page.blocks ?? []
        if (page.blocks && page.blocks.length > 0) {
            blocks.value = page.blocks.map((block) => ({
                type: block.type,
                id: block.id ?? generateBlockKey(),
            }))
        }
        seoData.value = {
            status: page.status,
            title: withAllLocales(page.title),
            description: withAllLocales(page.description),
            slug: withAllLocales(page.slug),
            og_title: withAllLocales(page.og_title),
            og_description: withAllLocales(page.og_description),
            og_image: page.og_image ?? null,
            focus_keyword: withAllLocales(page.focus_keyword),
        }
        seoBus.emit({ type: 'init', data: seoData.value })
    }
})
</script>
